import { DatabaseFieldType } from '@databases/types/database';

import aiSummaryFieldType from './field-types/aiSummary.ts';
import attachmentFieldType from './field-types/attachment.ts';
import backlinkFieldType from './field-types/backlink.ts';
import checkboxFieldType from './field-types/checkbox.ts';
import dateFieldType from './field-types/date.ts';
import entryDetailsFieldType from './field-types/entryDetails.ts';
import entryLinkFieldType from './field-types/entryLink.ts';
import hyperlinkFieldType from './field-types/hyperlink.ts';
import jiraIssueFieldType from './field-types/jiraIssue.ts';
import jiraIssueDetailsFieldType from './field-types/jiraIssueDetails.ts';
import numberFieldType from './field-types/number.ts';
import selectFieldType from './field-types/select.ts';
import textFieldType from './field-types/text.ts';
import userFieldType from './field-types/user.ts';
import type { FieldType } from './field-types.ts';

/**
 * Page field types are loaded via require() because they depend on
 * @databases/connections-api, which is not a declared dependency of
 * @databases/utils (adding it would create a package cycle).
 *
 * In production, the FieldTypeRegistry is populated at app bootstrap via
 * the service container, so these require() calls are never reached.
 * They only execute as a fallback in tests where the service container
 * is not initialized. See getFieldTypeRegistry() in field-types.ts.
 */
function loadPageFieldTypes(): Record<string, unknown> {
  try {
    return {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      [DatabaseFieldType.PAGE_DETAILS]: require('./field-types/pageDetails.ts').default,
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      [DatabaseFieldType.PAGE_LABELS]: require('./field-types/pageLabels.ts').default,
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      [DatabaseFieldType.PAGE_LINK]: require('./field-types/pageLink.ts').default,
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      [DatabaseFieldType.PAGE_STATUS]: require('./field-types/pageStatus.ts').default
    };
  } catch {
    return {};
  }
}

export function buildFieldTypeRegistry(): Record<
  DatabaseFieldType,
  FieldType<DatabaseFieldType>
> {
  return {
    [DatabaseFieldType.AI_SUMMARY]: aiSummaryFieldType,
    [DatabaseFieldType.ATTACHMENT]: attachmentFieldType,
    [DatabaseFieldType.BACKLINK]: backlinkFieldType,
    [DatabaseFieldType.CHECKBOX]: checkboxFieldType,
    [DatabaseFieldType.DATE]: dateFieldType,
    [DatabaseFieldType.ENTRY_DETAILS]: entryDetailsFieldType,
    [DatabaseFieldType.ENTRY_LINK]: entryLinkFieldType,
    [DatabaseFieldType.HYPERLINK]: hyperlinkFieldType,
    [DatabaseFieldType.JIRA_ISSUE]: jiraIssueFieldType,
    [DatabaseFieldType.JIRA_ISSUE_DETAILS]: jiraIssueDetailsFieldType,
    [DatabaseFieldType.NUMBER]: numberFieldType,
    [DatabaseFieldType.SELECT]: selectFieldType,
    [DatabaseFieldType.TEXT]: textFieldType,
    [DatabaseFieldType.USER]: userFieldType,
    ...loadPageFieldTypes()
  } as Record<DatabaseFieldType, FieldType<DatabaseFieldType>>;
}
